import { Suspense, useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { matchPath, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  Ellipsis,
  Languages,
  LogOut,
  Monitor,
  MonitorPlay,
  Moon,
  Sun,
  UserRound,
} from 'lucide-react';
import type { Permission } from '@iptv/shared-types';
import { cn } from '../cn.js';
import { useAuth } from './auth.js';
import { useTheme, type ThemePref } from './theme.js';
import { PageSkeleton } from '../components/primitives.js';
import { Modal } from '../components/feedback.js';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '../components/navigation.js';
import { useMediaQuery } from '../components/hooks.js';

// ------------------------------------------------------------------ Small chrome controls

const chromeButton =
  'pressable focus-ring tap-target inline-flex h-10 items-center justify-center gap-2 rounded-full text-sm font-medium text-muted ' +
  'hover:bg-[var(--control-bg-hover)] hover:text-fg';

export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const next = i18n.language === 'ar' ? 'en' : 'ar';
  return (
    <button
      type="button"
      onClick={() => void i18n.changeLanguage(next)}
      className={cn(chromeButton, 'px-3')}
      title={t('common.language')}
    >
      <Languages className="size-4.5" aria-hidden />
      <span lang={next}>{next === 'ar' ? t('common.arabic') : t('common.english')}</span>
    </button>
  );
}

const themeIcon: Record<ThemePref, ComponentType<{ className?: string }>> = {
  system: Monitor,
  light: Sun,
  dark: Moon,
};

export function ThemeToggle() {
  const { t } = useTranslation();
  const [pref, , set] = useTheme();
  const Icon = themeIcon[pref];
  return (
    <Menu
      label={t('common.theme')}
      className="w-52"
      trigger={(p) => (
        <button
          type="button"
          {...p}
          className={cn(chromeButton, 'w-10')}
          aria-label={t('common.theme')}
          title={t('common.theme')}
        >
          <Icon className="size-4.5" aria-hidden />
        </button>
      )}
    >
      <MenuLabel>{t('common.theme')}</MenuLabel>
      <MenuItem icon={Monitor} checked={pref === 'system'} onSelect={() => set('system')}>
        {t('common.themeSystem')}
      </MenuItem>
      <MenuItem icon={Sun} checked={pref === 'light'} onSelect={() => set('light')}>
        {t('common.themeLight')}
      </MenuItem>
      <MenuItem icon={Moon} checked={pref === 'dark'} onSelect={() => set('dark')}>
        {t('common.themeDark')}
      </MenuItem>
    </Menu>
  );
}

export function BrandMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'relative grid size-9 shrink-0 place-items-center rounded-[12px] text-white glass-rim',
        'bg-[linear-gradient(140deg,color-mix(in_srgb,var(--ui-brand-solid)_80%,white),var(--ui-brand-solid)_55%,color-mix(in_srgb,var(--ui-brand-solid)_75%,black))]',
        'shadow-[0_4px_12px_-4px_color-mix(in_srgb,var(--ui-brand-solid)_60%,transparent)]',
        className,
      )}
      aria-hidden
    >
      <MonitorPlay className="size-[18px]" />
    </span>
  );
}

function initials(name: string | undefined) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '');
}

export function Avatar({ name, className }: { name?: string; className?: string }) {
  return (
    <span
      className={cn(
        'grid size-8 shrink-0 place-items-center rounded-full bg-brand-soft text-xs font-semibold text-brand uppercase shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--ui-brand)_18%,transparent)]',
        className,
      )}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <Menu
      label={t('nav.account')}
      trigger={(p) => (
        <button
          type="button"
          {...p}
          className={cn(chromeButton, 'w-10')}
          aria-label={t('nav.account')}
        >
          <Avatar name={user?.displayName} />
        </button>
      )}
    >
      <div className="flex items-center gap-3 px-3 pt-2 pb-2.5" role="presentation">
        <Avatar name={user?.displayName} className="size-10 text-sm" />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{user?.displayName}</div>
          <div className="ltr-iso truncate text-xs text-muted">{user?.email}</div>
        </div>
      </div>
      <MenuSeparator />
      <MenuItem icon={UserRound} onSelect={() => navigate('/account')}>
        {t('nav.account')}
      </MenuItem>
      <MenuItem icon={LogOut} danger onSelect={() => void signOut()}>
        {t('common.signOut')}
      </MenuItem>
    </Menu>
  );
}

// ------------------------------------------------------------------ Navigation model

export interface NavItem {
  to: string;
  label: string;
  /** Compact label for the phone/tablet tab bar; falls back to `label`. */
  shortLabel?: string;
  icon: ComponentType<{ className?: string }>;
  permission?: Permission;
  end?: boolean;
}

const isActive = (item: NavItem, pathname: string) =>
  matchPath({ path: item.to, end: item.end ?? false }, pathname) !== null;

// ------------------------------------------------------------------ Desktop sidebar

const ITEM_H = 44;
const ITEM_GAP = 4;

function Sidebar({ title, items }: { title: string; items: NavItem[] }) {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const active = items.findIndex((i) => isActive(i, pathname));

  return (
    <aside className="sticky top-0 hidden h-dvh w-[288px] shrink-0 p-3 lg:block">
      <nav
        aria-label={title}
        className="glass relative flex h-full flex-col rounded-panel px-3 pt-4 pb-3"
      >
        <div className="mb-6 flex items-center gap-3 px-2">
          <BrandMark />
          <span className="min-w-0 truncate text-base font-semibold">{title}</span>
        </div>

        <div className="relative min-h-0 flex-1 overflow-y-auto scrollbar-none">
          <ul className="relative flex flex-col" style={{ gap: ITEM_GAP }}>
            {active >= 0 && (
              <span
                aria-hidden
                className="glass-rim absolute inset-x-0 top-0 rounded-control bg-brand-soft shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--ui-brand)_16%,transparent)] transition-transform duration-[var(--dur-3)] ease-spring"
                style={{
                  height: ITEM_H,
                  transform: `translateY(${active * (ITEM_H + ITEM_GAP)}px)`,
                }}
              />
            )}
            {items.map(({ to, label, icon: Icon, end }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      'pressable focus-ring relative flex items-center gap-3 rounded-control px-3 text-sm',
                      isActive
                        ? 'font-semibold text-brand'
                        : 'text-muted hover:bg-[var(--control-bg)] hover:text-fg',
                    )
                  }
                  style={{ height: ITEM_H }}
                >
                  <Icon className="size-[18px] shrink-0" />
                  <span className="min-w-0 truncate">{label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-3 flex items-center gap-1 rounded-[18px] bg-[var(--control-bg)] p-1.5 shadow-[inset_0_0_0_1px_var(--ui-line)]">
          <NavLink
            to="/account"
            className={({ isActive }) =>
              cn(
                'pressable focus-ring flex min-w-0 flex-1 items-center gap-2.5 rounded-[12px] p-1.5 text-sm',
                isActive ? 'bg-surface shadow-sm' : 'hover:bg-[var(--control-bg-hover)]',
              )
            }
          >
            <Avatar name={user?.displayName} />
            <span className="min-w-0">
              <span className="block truncate font-medium">{user?.displayName}</span>
              <span className="block truncate text-xs text-muted">{t('nav.account')}</span>
            </span>
          </NavLink>
          <button
            type="button"
            onClick={() => void signOut()}
            className="pressable focus-ring grid size-10 shrink-0 place-items-center rounded-[12px] text-muted hover:bg-[var(--control-bg-hover)] hover:text-danger"
            aria-label={t('common.signOut')}
            title={t('common.signOut')}
          >
            <LogOut className="size-4.5 rtl:rotate-180" />
          </button>
        </div>
      </nav>
    </aside>
  );
}

// ------------------------------------------------------------------ Phone / tablet bottom nav

function BottomNav({ items }: { items: NavItem[] }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const tablet = useMediaQuery('(min-width: 48rem)');
  const [moreOpen, setMoreOpen] = useState(false);

  const slots = tablet ? 7 : 5;
  const overflow = items.length > slots;
  const primary = overflow ? items.slice(0, slots - 1) : items;
  const extra = overflow ? items.slice(slots - 1) : [];
  const count = primary.length + (overflow ? 1 : 0);

  const primaryActive = primary.findIndex((i) => isActive(i, pathname));
  const extraActive = extra.some((i) => isActive(i, pathname));
  const active = primaryActive >= 0 ? primaryActive : extraActive ? primary.length : -1;

  useEffect(() => setMoreOpen(false), [pathname]);

  const tab =
    'pressable focus-ring relative z-[1] flex min-w-0 flex-col items-center justify-center gap-1 rounded-[20px] px-1 text-[11px] font-medium leading-none';

  return (
    <>
      <nav
        aria-label={t('common.mainNavigation')}
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[var(--z-nav)] px-3 lg:hidden"
        style={{
          paddingBottom: 'max(12px, var(--safe-bottom))',
          paddingLeft: 'max(12px, var(--safe-left))',
          paddingRight: 'max(12px, var(--safe-right))',
        }}
      >
        <div
          className="glass-thick pointer-events-auto relative mx-auto grid max-w-[560px] grid-flow-col auto-cols-fr rounded-[26px] p-1.5"
          style={{ height: 'var(--nav-h)' }}
        >
          {active >= 0 && (
            <span
              aria-hidden
              className="glass-rim absolute inset-y-1.5 start-1.5 rounded-[20px] bg-brand-soft transition-transform duration-[var(--dur-3)] ease-spring"
              style={{
                width: `calc((100% - 12px) / ${count})`,
                transform: `translateX(calc(${active} * 100% * var(--tab-dir, 1)))`,
              }}
            />
          )}
          {primary.map(({ to, label, shortLabel, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              aria-label={shortLabel && shortLabel !== label ? label : undefined}
              className={({ isActive }) =>
                cn(tab, isActive ? 'text-brand' : 'text-muted hover:text-fg')
              }
            >
              <Icon className="size-[22px] shrink-0" />
              <span className="max-w-full truncate">{shortLabel ?? label}</span>
            </NavLink>
          ))}
          {overflow && (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              className={cn(tab, extraActive ? 'text-brand' : 'text-muted hover:text-fg')}
            >
              <Ellipsis className="size-[22px] shrink-0" />
              <span className="max-w-full truncate">{t('common.more')}</span>
            </button>
          )}
        </div>
      </nav>

      {overflow && (
        <Modal
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
          title={t('common.more')}
          size="sm"
        >
          <ul className="-mx-2 space-y-1">
            {extra.map(({ to, label, icon: Icon, end }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      'pressable focus-ring flex min-h-13 items-center gap-3 rounded-lg px-2 text-base font-medium',
                      isActive ? 'bg-brand-soft text-brand' : 'hover:bg-[var(--control-bg)]',
                    )
                  }
                >
                  <span className="grid size-9 shrink-0 place-items-center rounded-[11px] bg-[var(--control-bg)] shadow-[inset_0_0_0_1px_var(--ui-line)]">
                    <Icon className="size-[18px]" />
                  </span>
                  <span className="min-w-0 flex-1 truncate">{label}</span>
                  <ChevronRight className="size-4 text-subtle rtl:rotate-180" aria-hidden />
                </NavLink>
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </>
  );
}

// ------------------------------------------------------------------ Shell

/** Becomes true once the page scrolls, so the header picks up its glass only when needed. */
function useScrolled() {
  const sentinel = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => setScrolled(!e?.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [sentinel, scrolled] as const;
}

const gutter =
  'pl-[max(1rem,var(--safe-left))] pr-[max(1rem,var(--safe-right))] ' +
  'sm:pl-[max(1.5rem,var(--safe-left))] sm:pr-[max(1.5rem,var(--safe-right))] lg:px-8 2xl:px-10';

export function AppShell({
  title,
  nav,
  banner,
}: {
  title: string;
  nav: NavItem[];
  banner?: ReactNode;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const location = useLocation();
  const [sentinel, scrolled] = useScrolled();

  const items = nav.filter((n) => !n.permission || can(n.permission));

  return (
    <div className="flex min-h-dvh">
      <a
        href="#main"
        className="skip-link glass-thick rounded-full px-4 py-2.5 text-sm font-semibold text-fg"
      >
        {t('common.skipToContent')}
      </a>

      <Sidebar title={title} items={items} />

      <div className="relative flex min-w-0 flex-1 flex-col">
        <div ref={sentinel} className="absolute inset-x-0 top-0 h-px" aria-hidden />
        <header
          className="sticky top-0 z-[var(--z-header)]"
          style={{ paddingTop: 'var(--safe-top)' }}
          data-scrolled={scrolled}
        >
          <div
            aria-hidden
            className={cn(
              'glass pointer-events-none absolute inset-0 rounded-none border-x-0 border-t-0 shadow-none transition-opacity duration-[var(--dur-3)] [&::before]:hidden',
              scrolled ? 'opacity-100' : 'opacity-0',
            )}
          />
          <div className={cn('relative flex h-16 items-center gap-2', gutter)}>
            <div className="flex min-w-0 items-center gap-2.5 lg:hidden">
              <BrandMark className="size-8 rounded-[10px]" />
              <span className="min-w-0 truncate text-base font-semibold">{title}</span>
            </div>
            <div className="ms-auto flex items-center gap-0.5">
              <LanguageSwitcher />
              <ThemeToggle />
              <div className="lg:hidden">
                <UserMenu />
              </div>
            </div>
          </div>
        </header>

        {banner}

        <main
          id="main"
          tabIndex={-1}
          className={cn(
            'mx-auto w-full max-w-[1240px] flex-1 pt-2 pb-[calc(var(--bottom-chrome)+1.5rem)] outline-none lg:pt-4 lg:pb-12 3xl:max-w-[1440px]',
            gutter,
          )}
        >
          <div key={location.pathname} className="animate-page">
            <Suspense fallback={<PageSkeleton label={t('common.loading')} />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

      <BottomNav items={items} />
    </div>
  );
}
